import { gql, useQuery } from "@apollo/client";
import { MessageBar } from "@fluentui/react";
import React, { useContext } from "react";
import AppContext, { IFilters } from "../../../AppContext";
import Counter from "../../../components/Counter";
import Title from "../../../components/Title";
import { Entorno } from "../../../utils/constants";
import "./style.scss";
import { useTranslation } from "react-i18next";

export const titleEn = "Market size";
export const title = "Tamaño del mercado";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: false,
  hasTicketMedio: false,
  isDesglose: false,
  showEntorno: true,
  showSegmentos: true,
  showMyData: false,
};

const Graph = () => {
  const { semanas, filters, user } = useContext(AppContext);
  const { t } = useTranslation();
  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
    ) {
      graph(
        Graph: "Tamano del mercado"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
        }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 1,
      segment:
        filters.segmentType === 0
          ? filters?.segmentos[filters?.segment]
          : filters?.especialidades[filters?.segment],
      segmentType: filters.segmentType,
    },
  });

  let errorMsg = t("An-error-has-occur-when-recovering-data") as string;
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;
  let emptyData = {
    Total: {
      Ventas: { Total: 0, Tendencia: 0 },
      Tickets: { Total: 0, Tendencia: 0 },
      TicketMedio: { Total: 0, Tendencia: 0 },
      PuntosDeVenta: { Total: 0, Tendencia: 0 },
      VentaMediaLocal: { Total: 0, Tendencia: 0 },
    },
    Comparable: {
      Ventas: { Total: 0, Tendencia: 0 },
      Tickets: { Total: 0, Tendencia: 0 },
      TicketMedio: { Total: 0, Tendencia: 0 },
      PuntosDeVenta: { Total: 0, Tendencia: 0 },
      VentaMediaLocal: { Total: 0, Tendencia: 0 },
    },
    Aperturas: { Total: 0, Tendencia: 0 },
    Error: false,
  };
  let data = emptyData;
  let exportData: any = [];

  if (!!qry?.data) {
    try {
      data = qry?.data?.graph;
      if (!data) throw new Error(errorMsg);
      if (data?.Error) throw new Error(`${data.Error}`);

      exportData = getExportData(data, semanas[0].year === 2019, t);
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }

  const isComparable = filters.entorno === Entorno.Comparable;
  const exportTitle = t("Market-size") as string;
  return (
    <>
      <Title
        title={t("Market-size") as string}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title: exportTitle,
          leyenda: "",
          transposed: true,
        }}
      />
      {error && <MessageBar isMultiline={true}>{errorMsg}</MessageBar>}
      {!error && (
        <div className="counter-container">
          <Counter
            title={t("Sales")}
            value={
              isComparable
                ? data.Comparable.Ventas.Total
                : data.Total.Ventas.Total
            }
            growthValue={
              isComparable
                ? data.Comparable.Ventas.Tendencia
                : data.Total.Ventas.Tendencia
            }
            isLoading={loading}
            showDivisa
          />
          <Counter
            title={"Tickets"}
            value={
              isComparable
                ? data.Comparable.Tickets.Total
                : data.Total.Tickets.Total
            }
            growthValue={
              isComparable
                ? data.Comparable.Tickets.Tendencia
                : data.Total.Tickets.Tendencia
            }
            isLoading={loading}
          />
          <Counter
            title={t("Average-ticket")}
            value={
              isComparable
                ? data.Comparable.TicketMedio.Total
                : data.Total.TicketMedio.Total
            }
            growthValue={
              isComparable
                ? data.Comparable.TicketMedio.Tendencia
                : data.Total.TicketMedio.Tendencia
            }
            isLoading={loading}
            showDivisa
          />
          <Counter
            title={t("Net-openings")}
            value={data.Aperturas.Total}
            growthTitle={t("Difference")}
            isLoading={loading}
          />
          <Counter
            title={t("Points-of-sales")}
            value={
              isComparable
                ? data.Comparable.PuntosDeVenta.Total
                : data.Total.PuntosDeVenta.Total
            }
            growthValue={
              isComparable
                ? data.Comparable.PuntosDeVenta.Tendencia
                : data.Total.PuntosDeVenta.Tendencia
            }
            isLoading={loading}
          />
          {filters.entorno === 0 && (
            <Counter
              title={t("Average-sales-per-store")}
              value={
                isComparable
                  ? data.Comparable.VentaMediaLocal.Total
                  : data.Total.VentaMediaLocal.Total
              }
              growthValue={
                isComparable
                  ? data.Comparable.VentaMediaLocal.Tendencia
                  : data.Total.VentaMediaLocal.Tendencia
              }
              isLoading={loading}
              showDivisa
            />
          )}
        </div>
      )}
    </>
  );
};

export default Graph;

// Traduce el export usando t()
const getExportData = (data: any, isLeap: boolean, t: any) => {
  const L = {
    marketSize: t("Market-size") as string,
    absolute: t("Absolute") as string,
    sales: t("Sales") as string,
    tickets: t("Tickets") as string,
    avgTicket: t("Average-ticket") as string,
    avgSalesPerStore: t("Average-sales-per-store") as string,
    pointsOfSales: t("Points-of-sales") as string,
    netOpenings: t("Net-openings") as string,
  };

  const tendencias = {
    totales: {
      [L.sales]: { Total: data.Total.Ventas.Tendencia },
      [L.tickets]: { Total: data.Total.Tickets.Tendencia },
      [L.avgTicket]: { Total: data.Total.TicketMedio.Tendencia },
      [L.avgSalesPerStore]: { Total: data.Total.VentaMediaLocal.Tendencia },
      [L.pointsOfSales]: { Total: data.Total.PuntosDeVenta.Tendencia },
      [L.netOpenings]: { Total: data.Aperturas.Tendencia },
    },
    comparables: {
      [L.sales]: { Comparable: data.Comparable.Ventas.Tendencia },
      [L.tickets]: { Comparable: data.Comparable.Tickets.Tendencia },
      [L.avgTicket]: { Comparable: data.Comparable.TicketMedio.Tendencia },
      [L.avgSalesPerStore]: {
        Comparable: data.Comparable.VentaMediaLocal.Tendencia,
      },
      [L.pointsOfSales]: { Comparable: "-#" },
      [L.netOpenings]: { Comparable: "-#" },
    },
  };

  const absoluto = {
    totales: {
      [L.sales]: {
        Total: data.Total.Ventas.Total.toLocaleString("es-ES") + "€#",
      },
      [L.tickets]: {
        Total: data.Total.Tickets.Total.toLocaleString("es-ES") + "#",
      },
      [L.avgTicket]: {
        Total:
          data.Total.TicketMedio.Total.toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + "€#",
      },
      [L.avgSalesPerStore]: {
        Total: data.Total.VentaMediaLocal.Total.toLocaleString("es-ES") + "€#",
      },
      [L.pointsOfSales]: {
        Total: data.Total.PuntosDeVenta.Total.toLocaleString("es-ES") + "#",
      },
      [L.netOpenings]: {
        Total: data.Aperturas.Total.toLocaleString("es-ES") + "#",
      },
    },
    comparables: {
      [L.sales]: {
        Comparable:
          data.Comparable.Ventas.Total.toLocaleString("es-ES") + "€#",
      },
      [L.tickets]: {
        Comparable:
          data.Comparable.Tickets.Total.toLocaleString("es-ES") + "#",
      },
      [L.avgTicket]: {
        Comparable:
          data.Comparable.TicketMedio.Total.toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + "€#",
      },
      [L.avgSalesPerStore]: {
        Comparable:
          data.Comparable.VentaMediaLocal.Total.toLocaleString("es-ES") + "€#",
      },
      [L.pointsOfSales]: { Comparable: "-#" },
      [L.netOpenings]: { Comparable: "-#" },
    },
  };

  const exportData: any[] = [];

  exportData.push({
    [L.marketSize]: {
      [L.sales]: Object.assign(
        {},
        tendencias.totales[L.sales],
        isLeap ? {} : tendencias.comparables[L.sales]
      ),
      [L.tickets]: Object.assign(
        {},
        tendencias.totales[L.tickets],
        isLeap ? {} : tendencias.comparables[L.tickets]
      ),
      [L.avgTicket]: Object.assign(
        {},
        tendencias.totales[L.avgTicket],
        isLeap ? {} : tendencias.comparables[L.avgTicket]
      ),
      [L.avgSalesPerStore]: Object.assign(
        {},
        tendencias.totales[L.avgSalesPerStore],
        isLeap ? {} : tendencias.comparables[L.avgSalesPerStore]
      ),
      [L.pointsOfSales]: Object.assign(
        {},
        tendencias.totales[L.pointsOfSales],
        isLeap ? {} : tendencias.comparables[L.pointsOfSales]
      ),
      [L.netOpenings]: Object.assign(
        {},
        tendencias.totales[L.netOpenings],
        isLeap ? {} : tendencias.comparables[L.netOpenings]
      ),
    },
  });

  exportData.push({
    [`${L.marketSize} (${L.absolute})`]: {
      [L.sales]: Object.assign(
        {},
        absoluto.totales[L.sales],
        isLeap ? {} : absoluto.comparables[L.sales]
      ),
      [L.tickets]: Object.assign(
        {},
        absoluto.totales[L.tickets],
        isLeap ? {} : absoluto.comparables[L.tickets]
      ),
      [L.avgTicket]: Object.assign(
        {},
        absoluto.totales[L.avgTicket],
        isLeap ? {} : absoluto.comparables[L.avgTicket]
      ),
      [L.avgSalesPerStore]: Object.assign(
        {},
        absoluto.totales[L.avgSalesPerStore],
        isLeap ? {} : absoluto.comparables[L.avgSalesPerStore]
      ),
      [L.pointsOfSales]: Object.assign(
        {},
        absoluto.totales[L.pointsOfSales],
        isLeap ? {} : absoluto.comparables[L.pointsOfSales]
      ),
      [L.netOpenings]: Object.assign(
        {},
        absoluto.totales[L.netOpenings],
        isLeap ? {} : absoluto.comparables[L.netOpenings]
      ),
    },
  });

  return exportData;
};
